import { useCallback, useEffect, useState } from 'react';

// A section that can be folded away, and remembers whether it was.
//
// The Analytics page grew to sixteen sections, several of them tall tables the
// operator consults occasionally and scrolls past every day. "What's converting"
// alone is five cards. Folding them by default puts the answer to "how am I
// doing?" back on one screen without deleting anything: everything is one click
// away, and the click is remembered.
//
// A native <details> would give this for free but cannot persist, cannot animate,
// and drags its own marker styling across seven themes — so this is a button and
// a region, wired the way a disclosure is supposed to be: aria-expanded on the
// control, aria-controls pointing at the panel, and the panel unmounted when
// closed so a collapsed section costs nothing to render.
//
// `id` is the persistence key. Changing it resets the operator's choice, which
// is why it is a required, stable string rather than derived from the title.

const KEY = (id) => `hunter.section.${id}`;

function readOpen(id, fallback) {
  try {
    const v = localStorage.getItem(KEY(id));
    return v === null ? fallback : v === '1';
  } catch {
    // Private mode, disabled storage. The default is a fine answer.
    return fallback;
  }
}

export default function CollapsibleSection({
  id,
  title,
  hint,
  defaultOpen = false,
  summary = null,
  tone = 'muted',
  children,
}) {
  const [open, setOpen] = useState(() => readOpen(id, defaultOpen));

  // The key can change between renders (a section rendered per dimension), and
  // the operator's stored choice for the NEW key is the one that should win.
  useEffect(() => {
    setOpen(readOpen(id, defaultOpen));
  }, [id, defaultOpen]);

  const toggle = useCallback(() => {
    setOpen((cur) => {
      const next = !cur;
      try {
        localStorage.setItem(KEY(id), next ? '1' : '0');
      } catch {
        /* the in-memory state still works for this visit */
      }
      return next;
    });
  }, [id]);

  const panelId = `section-panel-${id}`;
  const t = tone === 'accent' ? 'text-brand-500' : 'text-slate-500';

  return (
    <section className="space-y-2">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={panelId}
        className="group flex w-full items-baseline gap-2 text-left"
      >
        <span
          aria-hidden="true"
          className={'shrink-0 text-[10px] text-slate-500 transition-transform motion-reduce:transition-none ' + (open ? 'rotate-90' : '')}
        >
          ▶
        </span>
        <span className="min-w-0">
          <span className={`text-xs uppercase tracking-wide ${t} group-hover:text-slate-300`}>{title}</span>
          {/* The one-line summary is the point of collapsing: a folded section
              still has to answer "is there anything in here for me today?" */}
          {summary != null && <span className="ml-2 text-xs text-slate-400">{summary}</span>}
          {hint && open && <span className="mt-0.5 block text-xs text-slate-500">{hint}</span>}
        </span>
        <span className="ml-auto shrink-0 text-[10px] text-slate-600 group-hover:text-slate-400">
          {open ? 'hide' : 'show'}
        </span>
      </button>
      {open && <div id={panelId}>{children}</div>}
    </section>
  );
}
